define('ordersDetailCompositionProofsheetView',[
  'app',
  'module',
  'marionette',
  'underscore',
  'pdfView',
  'settings',
  'modalsConfirmationView',
  'dropzone',
  'imageUtils',
  'compuzzProUtils',
  'pdfEditorView',
  'ordersDetailCompositionProofsheetHistoryView',
  'pimPicturePositionerLauncher',
  'entities/pim_product',
  'template!ordersDetailCompositionProofsheetView'
], function (
  App,
  Module,
  Marionette,
  _,
  PdfView,
  Settings,
  ConfirmationView,
  Dropzone,
  ImageUtils,
  CompuzzProUtils,
  PdfEditorView,
  HistoryView,
  PositionerLauncher,
  PimProduct,
  Tpl
) {
  'use strict';

  // http(s) URL whose path ends in an image extension.
  var IMAGE_URL_RE = /^https?:\/\/[^\s"'<>]+\.(png|jpe?g|gif|bmp|webp|svg)(\?[^\s"'<>]*)?$/i;
  // Non-image file extensions to skip even when the key looks image-ish.
  var NON_IMAGE_RE = /\.(pdf|json|xml|csv|txt|html?|docx?|xlsx?|pptx?|zip|rar|mp[34]|mov|avi|webm|otf|ttf|woff2?)(\?|#|$)/i;
  // Keys that mark a value (or a container of values) as picture data — the
  // image word, optionally followed by a common suffix (s / digits / Url / Uid /
  // Link / Path / SecId / Src …), anchored at the end of the key.
  var IMAGE_KEY_RE = /(picture|image|photo|thumb(?:nail)?|logo|visual|preview)(s|\d+|url|uri|uid|link|path|src|href|secid|fileuid)?$/i;
  // A plausible compuzz / file-server identifier.
  var UID_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{6,120}$/;

  function fileNameFromUrl(url) {
    var clean = url.split('?')[0].split('#')[0];
    var segment = clean.substring(clean.lastIndexOf('/') + 1);
    return segment || 'image';
  }

  // Ensure the picker's extension filter accepts this file name.
  function imageFileName(url) {
    var fn = fileNameFromUrl(url);
    return /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(fn) ? fn : (fn || 'image') + '.png';
  }

  // Walks an arbitrary JSON tree and collects every distinct product image it can
  // find. PIM products scatter picture URLs across nested relations (supplier
  // data, reseller data, option categories, options, variations, print areas…),
  // some as full URLs, some as extension-less file-server links, some as bare
  // file UIDs — so rather than chasing known fields we parse the whole payload
  // and keep anything that (by value or by the key it sits under) looks like an
  // image.
  function collectImageUrls(node, out, seen, ctxKey) {
    if (node === null || node === undefined) {
      return;
    }
    if (typeof node === 'string') {
      var raw = null;
      if (/^https?:\/\//i.test(node)) {
        if (IMAGE_URL_RE.test(node) ||
          (IMAGE_KEY_RE.test(ctxKey || '') && !NON_IMAGE_RE.test(node))) {
          raw = node;
        }
      } else if (IMAGE_KEY_RE.test(ctxKey || '') && UID_RE.test(node)) {
        raw = Settings.url('file', node);
      }
      if (raw) {
        var url = proxiedImageUrl(raw);
        if (!seen[url]) {
          seen[url] = true;
          out.push({ url: url, fileName: imageFileName(raw) });
        }
      }
      return;
    }
    if (_.isArray(node)) {
      _.each(node, function (value) { collectImageUrls(value, out, seen, ctxKey); });
    } else if (_.isObject(node)) {
      _.each(node, function (value, key) {
        // An image-ish container key stays the context for its primitives
        // (e.g. `images: [{ uid: '…' }]`, `addImages: [{ url: '…' }]`).
        collectImageUrls(value, out, seen, IMAGE_KEY_RE.test(key) ? key : ctxKey);
      });
    }
  }

  // The editor draws inserted images onto a <canvas>, so a cross-origin image
  // whose host sends no CORS headers would taint the canvas and break the PDF
  // export. Route any URL on a different origin than the dashboard through the
  // backend proxy (served from a compuzz origin with CORS), and leave
  // same-origin / relative URLs untouched.
  function proxiedImageUrl(rawUrl) {
    if (!rawUrl || !/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }
    var origin = window.location.protocol + '//' + window.location.host;
    if (rawUrl === origin || rawUrl.indexOf(origin + '/') === 0) {
      return rawUrl;
    }
    // Settings.url does not encode param values, so encode the URL ourselves.
    return Settings.url('compuzz2', 'urlproxy', { url: encodeURIComponent(rawUrl) });
  }

  // Turns a fetched PIM product into image-source-picker items. Each item only
  // needs a `.get(key)` accessor; `url` is absolute (proxied when cross-origin)
  // so the picker uses it as-is instead of resolving it as a file-server link.
  function extractPimProductImages(pimProduct) {
    var out = [];
    collectImageUrls(pimProduct.toJSON(), out, {});
    return _.map(out, function (attrs) {
      return {
        get: function (key) {
          return attrs[key];
        }
      };
    });
  }

  /** Why the PIM cannot make a sheet for this order, as the server said it. */
  function reasonText(reason) {
    switch (reason) {
      case 'NOT_PIM':
        return _.i18n('order.customProofsheet.reason.notPim', null, 'Not a PIM order.');
      case 'PRODUCT_NOT_FOUND':
        return _.i18n('order.customProofsheet.reason.productNotFound', null, 'Product not found in the PIM.');
      case 'NO_COMPOSITION':
        return _.i18n('order.customProofsheet.reason.noComposition', null, 'Nothing to print on this order.');
      case 'NO_POSITIONS':
        return _.i18n('order.customProofsheet.reason.noPositions', null,
          'No print area is placed on the product pictures — position them in the PIM.');
      default:
        return _.i18n('order.customProofsheet.reason.unknown', null, 'Not available for this order.');
    }
  }

  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'orders-detail-body-composition-proofsheet-view',

    ui: {
      content: '.content',
      imageContainer: '.image-ctn',
      loader: '.loader-ctn',
      uploadButtonContainer: '.content .upload-button-container',
      uploadButton: '.buttons .upload-button',
      uploadInput: '.content .upload-input',
      deleteButton: '.content .delete-button',
      zoomButton: '.content .zoom-button',
      downloadButton: '.content .download-button',
      copyButton: '.content .copy-button'
    },

    regions: {
      historyRegion: '.history-region'
    },

    modelEvents: {
      'change:proofsheetFileUid': 'render'
    },

    events: {
      'click .js-generate-pim-proofsheet': 'onGenerateFromPim',
      'click .btn-edit-custom-proofsheet': 'onEditCustomProofsheet',
      'click .btn-edit-custom-proofsheet-inline': 'onEditInline',
      'click .btn-create-custom-proofsheet-inline': 'onCreateInline',
      'click @ui.deleteButton': 'onDelete',
      'click @ui.uploadButtonContainer': 'onUpload',
      'click @ui.uploadButton': 'onUpload',
      'click @ui.downloadButton': 'onDownload',
      'click @ui.zoomButton': 'onZoom'
    },

    serializeData: function () {
      var data = {};

      data.src = '';

      if (!!Settings.configValue('advancedMode')) {
        data.advancedMode = true;
      } else {
        data.advancedMode = false;
      }

      data.hasProofsheet = !!this.model.get('proofsheetFileUid');
      data.pim = !!this.model.get('pim');

      if(this.model.get('proofsheetFileUid'))
        data.src = Settings.url('file', this.model.get('proofsheetFileUid')) + '&action=preview';

      return data;
    },

    onRender: function () {
      this.historyView = new HistoryView({
        model: this.model
      });
      this.getRegion('historyRegion').show(this.historyView);

      this.offerPimProofsheet();

      //----------
      if(this.model.get('proofsheetFileUid')) {
        this.ui.loader.css('display', '');
        this.ui.imageContainer.css('display', 'flex');
        this.ui.uploadButtonContainer.css('display', 'none');
      } else {
        this.ui.loader.css('display', '');
        this.ui.imageContainer.css('display', 'none');
        this.ui.uploadButtonContainer.css('display', 'flex');
      }

      //----------
      var that = this;

      this.ui.content.dropzone({
        url: Settings.url('compuzz'),
        autoProcessQueue: true,
        uploadMultiple: false,
        acceptedFiles: 'application/pdf',
        init: function () {
          this.on('addedfile', function(file) {
            that.showLoader(true);

            var formdata = new FormData();
            formdata.append('file', file);
            formdata.append('filename', file.name);
            var fileInfo = {
              fileType: file.type,
              fileExt: file.name.substring( file.name.lastIndexOf( '.' ) ),
              fileName: file.name,
              fileLastModified: file.lastModified,
              formData: formdata,
              base64Value: ''
            };
            App.request('order:upload-custom-proofsheet', that.model.get('purchaseOrderId'), fileInfo).then(_.bind(function () {
              CompuzzProUtils.getInstance().trigger('notificationCenter:compuzzProRefreshComposition');
              that.model.fetch();
            }, this));

          });
        },
        dictDefaultMessage: _.i18n('common.file.drag.drop')
      });
    },

    /**
     * A proofsheet made from the PIM alone: the ordered colour's picture with
     * the decoration where the print area was placed on it. Offered only when
     * someone has placed that area — the server decides, from the same plan
     * it will draw from — so the button never leads to "nothing to draw".
     */
    offerPimProofsheet: function () {
      if (!this.model.get('pim')) {
        return;
      }
      var $icon = this.$('.js-generate-pim-proofsheet');
      var $detail = this.$('.js-pim-proofsheet-detail');
      var label = _.i18n('order.customProofsheet.fromPim', null, 'Generate from the product picture');
      this.pimPlan = null;

      var off = function (why) {
        $icon.addClass('is-off').attr('title', label + ' — ' + why);
        $detail.text(why).attr('title', why);
      };

      App.request('pim:proofsheet:plan', this.model.get('purchaseOrderId'))
        .done(_.bind(function (plan) {
          if (this.isDestroyed) {
            return;
          }
          this.pimPlan = plan;
          if (plan && !plan.available && plan.reason === 'NO_POSITIONS') {
            // The button stays live: a click opens the positioner, and the
            // sheet is generated once the areas have a place.
            $icon.removeClass('is-off').addClass('is-pending').attr('title', label + ' — ' +
              _.i18n('order.customProofsheet.placeFirst', null,
                'the print areas are not placed on the product pictures yet: click to place them'));
            $detail.text('').attr('title', '');
            return;
          }
          if (!plan || !plan.available) {
            off(reasonText(plan ? plan.reason : null));
            return;
          }
          // Just the icon when it can be done; the particulars stay in its tooltip.
          var title = label + ': ' + (plan.placed || []).join(', ');
          if (plan.pictures && plan.pictures.length > 1) {
            title += ' (' + _.i18n('order.customProofsheet.fromPimPictures', null, '{n} pictures on one page')
              .replace('{n}', plan.pictures.length) + ')';
          }
          if (plan.unplaced && plan.unplaced.length) {
            title += ' — ' + _.i18n('order.customProofsheet.fromPimUnplaced', null, 'not on this picture:') +
              ' ' + plan.unplaced.join(', ');
          }
          $icon.removeClass('is-off is-pending').attr('title', title);
          $detail.text('').attr('title', title);
        }, this))
        .fail(_.bind(function (xhr) {
          if (this.isDestroyed) {
            return;
          }
          // A 404 here is the backend without this feature, not a missing order.
          off(xhr && xhr.status === 404 ?
            _.i18n('order.customProofsheet.fromPimMissing', null, 'Not available on this server yet.') :
            _.i18n('order.customProofsheet.fromPimFailed', null, 'The proofsheet could not be generated.') +
              (xhr && xhr.status ? ' (' + xhr.status + ')' : ''));
        }, this));
    },

    onGenerateFromPim: function () {
      var $button = this.$('.js-generate-pim-proofsheet');
      if ($button.hasClass('is-busy') || $button.hasClass('is-off')) {
        return;
      }
      if ($button.hasClass('is-pending')) {
        this.placeAreasThenOffer();
        return;
      }
      $button.addClass('is-busy');
      this.showLoader(true);
      App.request('pim:proofsheet:generate', this.model.get('purchaseOrderId'))
        .done(_.bind(function () {
          CompuzzProUtils.getInstance().trigger('notificationCenter:compuzzProRefreshComposition');
          this.model.fetch();
        }, this))
        .fail(_.bind(function () {
          if (this.isDestroyed) {
            return;
          }
          this.render();
          this.$('.js-pim-proofsheet-detail').text(
            _.i18n('order.customProofsheet.fromPimFailed', null, 'The proofsheet could not be generated.'));
        }, this));
    },

    /**
     * Nothing placed yet: the positioner opens on the order's product, right
     * here, and once a placement is saved the button is asked again whether
     * it can generate — no trip to the PIM and back.
     */
    placeAreasThenOffer: function () {
      var productSecId = this.model.get('productSecId');
      if (!productSecId) {
        return;
      }
      var $button = this.$('.js-generate-pim-proofsheet').addClass('is-busy');
      var pimProduct = App.request('pim:products:model', {id: productSecId});
      pimProduct.fetch()
        .done(_.bind(function () {
          if (this.isDestroyed) {
            return;
          }
          $button.removeClass('is-busy');
          PositionerLauncher.open(pimProduct, null, _.bind(function () {
            if (!this.isDestroyed) {
              this.offerPimProofsheet();
            }
          }, this));
        }, this))
        .fail(_.bind(function () {
          if (!this.isDestroyed) {
            $button.removeClass('is-busy');
            this.$('.js-pim-proofsheet-detail').text(
              _.i18n('order.customProofsheet.reason.productNotFound', null, 'Product not found in the PIM.'));
          }
        }, this));
    },

    onEditCustomProofsheet: function () {
      this.trigger('editCustomProofsheet');
    },

    onEditInline: function () {
      var uid = this.model.get('proofsheetFileUid');
      if (!uid)
        return;

      var poi = this.model.get('purchaseOrderId');
      PdfEditorView.open({
        id: poi,
        url: Settings.url('file', uid),
        fileName: poi + '.pdf',
        title: _.i18n('pdfEditor.title', null, 'PDF editor') + ': ' + poi,
        imageSources: this.buildImageSources(),
        referenceImages: this.buildReferenceImages(),
        onSave: _.bind(this.uploadEditedProofsheet, this)
      });
    },

    onCreateInline: function () {
      var poi = this.model.get('purchaseOrderId');
      PdfEditorView.open({
        id: poi,
        blank: true,
        fileName: poi + '.pdf',
        title: _.i18n('pdfEditor.createTitle', null, 'Create proofsheet') + ': ' + poi,
        imageSources: this.buildImageSources(),
        referenceImages: this.buildReferenceImages(),
        onSave: _.bind(this.uploadEditedProofsheet, this)
      });
    },

    // The print-area preview thumbnails shown in the editor as read-only
    // reference images, so the user can see where the logo sits on the product
    // while editing the proofsheet. These are the print-zone pictures (the bare
    // product photos with the print area marked), the same images the
    // composition tab renders as `.printarea-preview-thumb` — not the composited
    // snapshot. The pictures are fetched by the parent composition tab and
    // handed down via options.pzPictures (keyed by print-zone id).
    buildReferenceImages: function () {
      var compositions = this.model.get('compositions') || [];
      var pzPictures = this.options.pzPictures || {};
      var refs = [];
      _.each(compositions, function (compo) {
        if (!compo) {
          return;
        }
        var pictures = pzPictures[compo.pzId] || [];
        var label = compo.paName ? (compo.paCode + ' - ' + compo.paName) : (compo.paCode || '');
        _.each(pictures, function (picture) {
          if (picture && picture.imageUrl) {
            refs.push({ url: picture.imageUrl, label: label });
          }
        });
      });
      return refs;
    },

    // Image sources for the editor's "insert image" picker: the order's own
    // attachments plus (when resolvable from the order) the customer's and the
    // product's attachments. Models are fetched lazily and cached per source.
    buildImageSources: function () {
      var order = this.model;
      var sources = [];

      sources.push({
        key: 'order',
        label: _.i18n('pdfEditor.source.order', null, 'Order'),
        loader: function (first, pageSize) {
          return order.getAttachments(first, pageSize);
        }
      });

      var endUserId = order.get('endUserId');
      if (endUserId) {
        var customer = App.request('customers:entity:end-user-id', endUserId);
        var customerReady = null;
        sources.push({
          key: 'customer',
          label: _.i18n('pdfEditor.source.customer', null, 'Customer'),
          loader: function (first, pageSize) {
            if (!customerReady)
              customerReady = customer.fetch();
            return customerReady.then(function () {
              return customer.getAttachments(first, pageSize);
            });
          }
        });
      }

      var productSecId = order.get('productSecId');
      if (productSecId) {
        if (order.get('pim')) {
          // PIM products have no attachment endpoint; their pictures live as
          // URLs inside the product payload, so fetch it once and scrape them.
          var pimProduct = App.request('pim:products:model', { id: productSecId });
          var pimReady = null;
          var pimImages = [];
          sources.push({
            key: 'product',
            label: _.i18n('pdfEditor.source.product', null, 'Product'),
            loader: function (first, pageSize) {
              if (!pimReady) {
                pimReady = pimProduct.fetch().then(function () {
                  pimImages = extractPimProductImages(pimProduct);
                });
              }
              return pimReady.then(function () {
                return pimImages.slice(first, first + pageSize);
              });
            }
          });
        } else {
          var product = App.request('products-rel:model', { secId: productSecId });
          var productReady = null;
          sources.push({
            key: 'product',
            label: _.i18n('pdfEditor.source.product', null, 'Product'),
            loader: function (first, pageSize) {
              if (!productReady)
                productReady = product.fetch();
              return productReady.then(function () {
                // loadFromAllLevel = true also pulls the images attached to the
                // product's option categories, options and variations.
                return product.getAttachments(first, pageSize, true);
              });
            }
          });
        }
      }

      return sources;
    },

    uploadEditedProofsheet: function (file) {
      this.showLoader(true);

      var formdata = new FormData();
      formdata.append('file', file);
      formdata.append('filename', file.name);

      App.request('order:upload-custom-proofsheet', this.model.get('purchaseOrderId'), {
        formData: formdata
      }).then(_.bind(function () {
        CompuzzProUtils.getInstance().trigger('notificationCenter:compuzzProRefreshComposition');
        this.model.fetch();
      }, this));
    },

    onUpload: function () {
      this.ui.content.click();
    },

    showLoader: function () {
      this.ui.imageContainer.css('display', 'none');
      this.ui.uploadButtonContainer.css('display', 'none');
      this.ui.loader.css('display', 'flex');
    },

    onDownload: function () {
      var url = Settings.url('file', this.model.get('proofsheetFileUid'));
      if(url === '')
        return;

      var fileName = this.model.get('purchaseOrderId') + '.pdf';

      var anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.download = fileName;
      anchor.click();
    },

    onDelete: function () {
      var uid = this.model.get('proofsheetFileUid');
      if(uid === '')
        return;

      ConfirmationView.prototype.open({
        text: _.i18n('orders.detail.composition.proofsheet.delete.confirmation'),
        onConfirm: _.bind(function () {
          App.request('order:delete-custom-proofsheet', this.model.get('purchaseOrderId')).then(_.bind(function () {
            this.model.fetch();
          }, this));
        }, this)
      });
    },

    onZoom: function () {
      PdfView.prototype.open({
        url: Settings.url('file', this.model.get('proofsheetFileUid')),
        title: 'Proofsheet: ' + this.model.get('purchaseOrderId')
      });
    }
  });
});

